import { useState } from "react";
import { z } from "zod";
import { updatePassword, UpdatePasswordInput } from "@aws-amplify/auth";
import { Box, Button, Stack, Typography } from "@mui/material";

import { passwordSchema } from "../../../schemas/userSchema";
import { handleError } from "../../../utils/error/handleError";
import { useGlobalSignout } from "../../../hooks/useGlobalSignout";
import { useAppNavigate } from "../../../routes/useAppNavigate";
import { notify } from "../../../lib/notify";
import Layout from "../../templates/Layout";
import FormLayout from "../../Layout/FormLayout";
import { useFormWithSchema } from "../../../hooks/useFormWithSchema";
import LoadingButton from "../../Button/LoadingButton";
import PasswordTextField from "../../Form/PasswordTextField";
import ConfirmSetPasswordModal from "./ConfirmSetPasswordModal";
import { useModal } from "../../../hooks/useModal";

const SetPasswordSchema = z
  .object({
    oldPassword: passwordSchema,
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "パスワードが一致しません",
    path: ["confirmPassword"],
  })
  .refine((data) => data.oldPassword !== data.newPassword, {
    message: "現在のパスワードと同じパスワードです",
    path: ["newPassword"],
  });

type SetPasswordInputType = z.infer<typeof SetPasswordSchema>;

const SetPasswordPage = () => {
  const { signOut } = useGlobalSignout();
  const { publicNavigate, backNavigate } = useAppNavigate();
  const confirmModal = useModal();
  const [formData, setFormData] = useState<SetPasswordInputType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (data: SetPasswordInputType) => {
    setFormData(data);
    confirmModal.onOpen();
  };

  const setNewPassword = async () => {
    if (!formData) return;
    setIsSubmitting(true);
    try {
      const input: UpdatePasswordInput = {
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword,
      };

      await updatePassword(input);

      await signOut();
      notify("パスワードを変更しました。再ログインしてください。");
      publicNavigate.login({ replace: true });
    } catch (error) {
      let errorMessage = "エラーが発生しています。再度お試しください。";
      if (error instanceof Error && error.name === "NotAuthorizedException") {
        errorMessage = "ログインID、またはパスワードが間違っています。";
      }
      handleError(error, {
        overrideMessage: errorMessage,
      });
      confirmModal.onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const {
    formState: { errors },
    register,
    onSubmitHandler,
  } = useFormWithSchema(SetPasswordSchema, onSubmit);

  return (
    <Layout title="パスワード変更">
      <FormLayout toolbar={<Typography>パスワード変更</Typography>}>
        <>
          <Box component={"form"} onSubmit={onSubmitHandler} noValidate>
            <Stack spacing={3}>
              <PasswordTextField
                label="現在のパスワード"
                {...register("oldPassword")}
                error={!!errors.oldPassword}
                helperText={errors.oldPassword?.message}
                name="oldPassword"
                type="text"
                fullWidth
                size="small"
              />
              <Box>
                <PasswordTextField
                  label="新しいパスワード"
                  {...register("newPassword")}
                  error={!!errors.newPassword}
                  helperText={errors.newPassword?.message}
                  fullWidth
                  size="small"
                />
                <Typography
                  sx={{ mt: 1 }}
                  variant="passwordHint"
                  color={"text.secondary"}
                >
                  {
                    "パスワードは8文字以上20文字以内で、\n大文字・小文字・数字を含む英数字と、記号「-」「_」「.」が使えます。"
                  }
                </Typography>
              </Box>
              <PasswordTextField
                label="新しいパスワード（確認用）"
                {...register("confirmPassword")}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                name="confirmPassword"
                type="text"
                fullWidth
                size="small"
              />
            </Stack>
            <Stack
              direction={"row"}
              justifyContent={"flex-end"}
              sx={{ py: 4 }}
              spacing={2}
            >
              <Button
                onClick={() => backNavigate()}
                variant="outlined"
                sx={{ minWidth: "120px" }}
              >
                キャンセル
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                sx={{ minWidth: "120px" }}
              >
                変更
              </LoadingButton>
            </Stack>
          </Box>
          <ConfirmSetPasswordModal
            open={confirmModal.open}
            onClose={confirmModal.onClose}
            onConfirm={setNewPassword}
            isLoading={isSubmitting}
          />
        </>
      </FormLayout>
    </Layout>
  );
};

export default SetPasswordPage;
